import React, { Fragment, useEffect, useState } from "react";
import { ResponsiveHeader } from "pages/_components/header";
import { useDispatch, useSelector } from "react-redux";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import * as i18n from "util/i18n";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import Button from "pages/_components/Button";
import { actions as afipActions } from "reducers/afipPayments";
import TextField from "pages/_components/fields/TextField";
import Checkbox from "pages/_components/Checkbox";
import { actions as accountsActions } from "reducers/accounts";
import * as configUtils from "util/config";
import * as Yup from "yup";
import useSchema from "hooks/useSchema";
import gaEventTracker, { events } from "util/gaEventTracker";

const FORM_ID = "afipPayments.account";

const AfipAccontSelect = () => {
    const { afipTaxForm, afipPaymentData, fetching: fetchingAfipPayments } = useSelector((state) => state.afipPayments);
    const { fetching, accounts } = useSelector((state) => state.accounts);
    const { stringRequired } = useSchema();
    const dispatch = useDispatch();

    const initialValues = {
        debitAccount: "" || afipPaymentData.debitAccount,
        amount: "" || afipPaymentData.amount,
    };
    if (afipTaxForm?.taxForms?.length > 0) {
        afipTaxForm.taxForms.forEach((element) => {
            initialValues[`check${element.code}`] = true;
        });
        afipTaxForm.taxForms.forEach((element) => {
            initialValues[`input${element.code}`] = `${i18n.get("currency.label.ARS")} ${element.amount}`;
        });
    }
    const afipAccountsValidation = Yup.lazy(() => {
        const validate = {
            debitAccount: stringRequired,
        };
        return Yup.object().shape(validate);
    });

    const maskAccountNumber = (accountNumber) => {
        const mask = "••••";
        if (accountNumber) {
            return mask + accountNumber.slice(-4);
        }
        return mask;
    };

    const getLabel = (account) => {
        let accountValue = "";
        if (account?.accountType && account?.accountNumber) {
            accountValue = `${i18n.get(`accounts.accountType.${account?.accountType}`)} ${maskAccountNumber(
                account?.accountNumber,
            )} ${account?.accountType} ${i18n.get("currency.label.ARS")}`;
        }
        return accountValue;
    };

    const totalTaxt = afipTaxForm?.taxForms?.reduce((accum, item) => accum + item.amount, 0);
    const [totalTaxSelected, setTotalTaxSelected] = useState();

    useEffect(() => {
        dispatch(accountsActions.listAccounts());
    }, []);
    const mapData = accounts.filter((account) => account?.currency === configUtils.get("afip.currency.default", "ARS"));

    const accountAfipOptions = () =>
        mapData?.map((account) => ({
            balance: { currency: account?.currency, quantity: account?.availableBalance },
            currency: account?.currency,
            frequentDestination: null,
            id: account?.productId,
            label: getLabel(account),
            minimumPayment: 0,
            permissions: Object.keys(account?.permissions),
            statementsLoan: null,
            totalPayment: 0,
            type: account?.accountType,
        }));

    useEffect(() => {
        setTotalTaxSelected(Number(totalTaxt).toFixed(2));
    }, totalTaxt);

    const handleSubmit = (values) => {
        const taxFormData = [];
        Object.entries(values).forEach((entry) => {
            const [key, value] = entry;
            if (key.includes("input")) {
                const formCode = key.substring(5);
                if (values[`check${formCode}`]) {
                    taxFormData.push({ formCode, taxValue: value });
                }
            }
        });
        const taxForm = afipTaxForm.taxForms
            .map((element) => ({
                code: element.code,
                amount: element.amount,
            }))
            .filter((_element) => values[`check${_element.code}`]);

        let formTax;
        if (taxForm.length > 0) {
            formTax = taxForm.reduce((acc, curr) => {
                acc[curr.code] = curr.amount;
                return acc;
            }, {});
        }
        gaEventTracker.triggerEvent(events.pagos_servicios_enviar({ formulario: taxForm }));

        dispatch(
            afipActions.afipBarCodeGeneratorReducer(
                afipPaymentData.form,
                afipPaymentData.valuesNoForm,
                taxForm,
                afipTaxForm.hash,
            ),
        );
        dispatch(
            afipActions.setAfipPaymentData({
                ...afipPaymentData,
                debitAccount: values.debitAccount,
                amount: totalTaxSelected,
                formValues: values,
                taxFormData,
                taxForm: formTax,
            }),
        );
    };

    const handleCheck = (e, amount) => {
        if (e) {
            setTotalTaxSelected((Number(totalTaxSelected) + Number(amount)).toFixed(2));
        } else {
            setTotalTaxSelected((Number(totalTaxSelected) - Number(amount)).toFixed(2));
        }
    };

    const handleTotal = (e) => {
        const dataToReduce = afipTaxForm?.taxForms?.filter((form) => `input${form?.code}` !== e.target.name);
        const tatToCalculate = dataToReduce?.reduce((accum, item) => accum + item.amount, 0);
        let totalTax;
        if (e?.target?.value?.includes("$ ")) {
            const myArray = e.target.value.split(" ");
            totalTax = tatToCalculate + Number(myArray[1]);
        } else if (e?.target?.value?.includes("$")) {
            totalTax = tatToCalculate + +e.target.value.substring(1);
        } else {
            totalTax = tatToCalculate + +e.target.value;
        }
        setTotalTaxSelected(Number(totalTax));
    };

    return (
        <Fragment>
            <Notification scopeToShow="newAfip.account" />
            <ResponsiveHeader id="afipPayments.newAfip.title" />
            <MainContainer showLoader={fetching || fetchingAfipPayments}>
                <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={afipAccountsValidation}>
                    {({ values }) => (
                        <Form>
                            <Row>
                                {afipTaxForm?.taxForms?.map((tax) => (
                                    <>
                                        <Col xs={12} md={6}>
                                            <Field
                                                component={Checkbox}
                                                idForm={FORM_ID}
                                                name={`check${tax.code}`}
                                                labelText={tax.description}
                                                disabled={tax.isRequerid === "Y"}
                                                checked
                                                labelFromService
                                                value={`${i18n.get("currency.label.ARS")} ${tax.amount}`}
                                                onChange={(e) => handleCheck(e, tax.amount)}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Field
                                                idForm={FORM_ID}
                                                name={`input${tax.code}`}
                                                component={TextField}
                                                hidePlaceholder
                                                type="text"
                                                disabled={tax.isEditable === "N"}
                                                hideLabel
                                                handleOnChange={(e) => handleTotal(e)}
                                            />
                                        </Col>
                                    </>
                                ))}
                            </Row>
                            <Row>
                                <>
                                    <Col xs={12}>
                                        <Field
                                            component={ProductSelector}
                                            idForm={FORM_ID}
                                            name="debitAccount"
                                            label={i18n.get("afip.debit.account.title")}
                                            placeholder={mapData && getLabel(mapData[0])}
                                            data={{ options: accountAfipOptions(), emptyOptionLabel: "" }}
                                        />
                                    </Col>
                                    {totalTaxSelected && (
                                        <Col xs={12} md={6}>
                                            <Field
                                                idForm={FORM_ID}
                                                name="amount"
                                                component={TextField}
                                                labelText="afip.indications"
                                                label="afip.indications"
                                                hidePlaceholder
                                                type="text"
                                                disabled
                                                hideLabel
                                                value={`${i18n.get("currency.label.ARS")} ${totalTaxSelected}`}
                                                maxLength="500"
                                            />
                                        </Col>
                                    )}
                                </>
                            </Row>
                            {totalTaxSelected && (
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="afip.forms.pay.send"
                                            disabled={!values.debitAccount}
                                        />
                                    </Col>
                                </Row>
                            )}
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </Fragment>
    );
};

export default AfipAccontSelect;
